/**
 * =============================================================================
 * Hook: useReferralCommissionsPaginated
 * Versão: 1.0.0
 *
 * Descrição:
 *   Hook server-side para listagem paginada de comissões (affiliate_commissions)
 *   do tenant ativo. Espelha o padrão de `useReferralPayoutsPaginated` /
 *   `useFinanceiroCobrancasPaginated`:
 *
 *   - Chama a RPC `get_referral_commissions_paginated`, que devolve em UMA
 *     única chamada: linhas paginadas + total_count + KPIs globais
 *     (window functions).
 *   - 100% server-side: nenhum SELECT direto na tabela é feito a partir do
 *     frontend, evitando vazamento de dados sensíveis e o limite de 1k rows
 *     do client.
 *
 * Release notes:
 *   v1.0.0 - Criação do hook (substitui a query de listagem do
 *            `useReferralCommissionsAdmin`, que passa a ser deprecada).
 * =============================================================================
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

// Logger centralizado para depuração
const logger = createLogger('ReferralCommissionsPaginated');

export type CommissionStatus =
  Database['public']['Enums']['affiliate_commission_status'];
export type RewardType =
  Database['public']['Enums']['affiliate_reward_type'];

/**
 * Estrutura flat retornada pela RPC `get_referral_commissions_paginated`.
 * Otimizada para uso direto em tabelas (sem embeds aninhados).
 */
export interface PaginatedCommission {
  // Dados da comissão
  id: string;
  tenant_id: string;
  status: CommissionStatus;
  reward_type: RewardType;
  reward_value: number;
  base_value: number;
  currency: string;
  points_earned: number;
  created_at: string;
  approved_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  notes: string | null;
  payout_id: string | null;
  affiliate_id: string;
  referral_id: string;
  campaign_id: string | null;

  // Campos do JOIN (flat)
  affiliate_full_name: string | null;
  affiliate_email: string | null;
  referral_lead_name: string | null;
  campaign_name: string | null;

  // Origem (v2.0.0): negócio / cliente / vendedor / programa
  deal_id: string | null;
  deal_title: string | null;
  customer_id: string | null;
  customer_name: string | null;
  seller_id: string | null;
  seller_name: string | null;
  /** v2.1.0 — avatar do vendedor (espelha padrão da listagem de Negócios). */
  seller_avatar_url: string | null;
  program_id: string | null;
  program_name: string | null;
  /** Total de pagamentos vinculados (links ativos + liberados). */
  payouts_count: number;

  // KPIs globais (mesmo valor em todas as rows)
  kpi_total_pendente: number;
  kpi_total_aprovada: number;
  kpi_total_paga: number;
  kpi_total_cancelada: number;
  kpi_count_pendente: number;
  kpi_count_total: number;

  // Paginação (window function)
  total_count: number;
}

/** Filtros aceitos pela listagem de comissões. */
export interface CommissionPaginatedFilters {
  status?: CommissionStatus | null;
  affiliate_id?: string | null;
  campaign_id?: string | null;
  reward_type?: RewardType | null;
  search?: string | null;
  data_criacao_de?: string | null;
  data_criacao_ate?: string | null;
  data_aprovacao_de?: string | null;
  data_aprovacao_ate?: string | null;
  data_pagamento_de?: string | null;
  data_pagamento_ate?: string | null;
  only_unpaid?: boolean | null;
  // Novos filtros (v2.0.0) — origem
  deal_id?: string | null;
  referral_id?: string | null;
  customer_id?: string | null;
  seller_id?: string | null;
  program_id?: string | null;
}

/** KPIs agregados retornados pela RPC. */
export interface CommissionKpis {
  totalPendente: number;
  totalAprovada: number;
  totalPaga: number;
  totalCancelada: number;
  countPendente: number;
  countTotal: number;
}

interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface PaginatedCommissionsResult {
  commissions: PaginatedCommission[];
  totalCount: number;
  totalPages: number;
  kpis: CommissionKpis;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

const EMPTY_KPIS: CommissionKpis = {
  totalPendente: 0,
  totalAprovada: 0,
  totalPaga: 0,
  totalCancelada: 0,
  countPendente: 0,
  countTotal: 0,
};

/**
 * Hook principal — listagem paginada de comissões (server-side).
 *
 * @param filters    Filtros aplicados pela RPC.
 * @param pagination Página e tamanho da página.
 */
export function useReferralCommissionsPaginated(
  filters: CommissionPaginatedFilters | undefined,
  pagination: PaginationParams,
): PaginatedCommissionsResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'referral-commissions-paginated',
      currentTenant?.id,
      filters,
      pagination,
    ],
    queryFn: async () => {
      logger.debug('Buscando comissões paginadas', { filters, pagination });

      // Casting necessário até `types.ts` ser regenerado com a nova RPC
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_commissions_paginated',
        {
          p_page: pagination.page,
          p_page_size: pagination.pageSize,
          p_tenant_hint: currentTenant?.id || null,
          p_status: filters?.status || null,
          p_affiliate_id: filters?.affiliate_id || null,
          p_campaign_id: filters?.campaign_id || null,
          p_reward_type: filters?.reward_type || null,
          p_search: filters?.search || null,
          p_data_criacao_de: filters?.data_criacao_de || null,
          p_data_criacao_ate: filters?.data_criacao_ate || null,
          p_data_aprovacao_de: filters?.data_aprovacao_de || null,
          p_data_aprovacao_ate: filters?.data_aprovacao_ate || null,
          p_data_pagamento_de: filters?.data_pagamento_de || null,
          p_data_pagamento_ate: filters?.data_pagamento_ate || null,
          p_only_unpaid: filters?.only_unpaid ?? false,
          // Filtros de origem (v2.0.0)
          p_deal_id: filters?.deal_id || null,
          p_referral_id: filters?.referral_id || null,
          p_customer_id: filters?.customer_id || null,
          p_seller_id: filters?.seller_id || null,
          p_program_id: filters?.program_id || null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar comissões paginadas', error);
        throw error;
      }

      const rows = (data || []) as PaginatedCommission[];
      const totalCount = Number(rows[0]?.total_count ?? 0);

      const kpis: CommissionKpis = rows.length
        ? {
            totalPendente: Number(rows[0]?.kpi_total_pendente ?? 0),
            totalAprovada: Number(rows[0]?.kpi_total_aprovada ?? 0),
            totalPaga: Number(rows[0]?.kpi_total_paga ?? 0),
            totalCancelada: Number(rows[0]?.kpi_total_cancelada ?? 0),
            countPendente: Number(rows[0]?.kpi_count_pendente ?? 0),
            countTotal: Number(rows[0]?.kpi_count_total ?? 0),
          }
        : EMPTY_KPIS;

      return {
        commissions: rows,
        totalCount,
        totalPages: Math.ceil(totalCount / pagination.pageSize) || 1,
        kpis,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    commissions: query.data?.commissions || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    kpis: query.data?.kpis || EMPTY_KPIS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['referral-commissions-paginated', currentTenant?.id],
      });
    },
  };
}
